import { memo, useRef, useState } from 'react';
import { useButton } from 'react-aria';

import { mergeRefs } from '../../lib/mergeRefs';
import { ToastManager } from '../../ToastManager';
import { ContextMenu } from '../common/context-menu/ContextMenu';
import { Glyph } from '../common/Glyph';
import { TextSmallMedium } from '../common/text/TextSmallMedium';
import { TextTiny } from '../common/text/TextTiny';

import { useMood } from '../../store/moods/useMoods.ts';
import { useApi } from '../../store/SelectedHomeyStore.hooks.ts';

import { glyph } from '../../theme/glyph';
import { theme } from '../../theme/theme';
import styles from './Mood.module.css';

import { useMixedColors } from './CanvasLiquidLinearCircle';
import { CanvasLiquidLinearCircle } from './CanvasLiquidLinearCircle';
import { MoodContextMenuContent } from './MoodContextMenuContent';
import { useMoodDragDrop } from './useMoodDragDrop';

export function Mood(props) {
  const buttonRef = useRef();
  const index = useRef(0);

  const { api } = useApi();
  const { mood } = useMood({ moodId: props.moodId });

  const circleSize = 28;
  const mixedColors = useMixedColors({ devices: mood?.devices });

  const [elements, setElements] = useState([]);

  const button = useButton(
    {
      elementType: 'div',
      onPress(event) {
        const key = index.current++;

        setElements((prevElements) => [
          ...prevElements,
          <div
            key={key}
            className={styles.pulseAnimation}
            style={{
              '--prop-circle-size': `${circleSize}px`,
              '--prop-background': generateCSSGradient(mixedColors),
            }}
            onAnimationEnd={() => {
              setElements((prevElements) =>
                prevElements.filter((element) => {
                  return parseInt(element.key, 10) !== key;
                }),
              );
            }}
          />,
        ]);

        api.moods
          .setMood({ id: props.moodId })
          .then(() => {})
          .catch(ToastManager.handleError);
      },
    },
    buttonRef,
  );

  const { dragRef, dropRef, isDragging } = useMoodDragDrop({
    moodId: props.moodId,
    isDisabled: props.isDragDropDisabled,
    onDrop: props.onDrop,
    onDragEnd: props.onDragEnd,
    onRequestSwap: props.onRequestSwap,
    render() {
      return (
        <div className={styles.root} data-is-drag-preview={true}>
          <CanvasLiquidLinearCircle id={mood.id} size={circleSize} mixedColors={mixedColors} />

          <div className={styles.section}>
            <div className={styles.title}>
              <TextSmallMedium className={styles.titleText}>{mood.name}</TextSmallMedium>
            </div>
            <div className={styles.subtitle}>
              <TextTiny className={styles.subtitleText} color={theme.color.text_light}>
                {Object.values(mood.devices).length} Lights
              </TextTiny>
            </div>
          </div>
        </div>
      );
    },
  });

  if (mood == null) {
    return (
      <div className={styles.root} title={props.moodId} data-is-interaction-disabled={true}>
        <Glyph
          size={theme.icon.size_medium}
          color={theme.icon.color_medium}
          source={glyph.core.exclamationmarkTriangleFill}
        />
      </div>
    );
  }

  function generateCSSGradient(mixedColors) {
    // Start the gradient string with the direction
    let gradientStr = 'linear-gradient(to top right';

    // Add each color stop to the gradient string
    mixedColors.forEach((color, index) => {
      const offset = (index / mixedColors.length) * 100;
      const colorStr = `hsla(${color.hue}, ${color.saturation}%, ${color.lightness}%, ${color.alpha}) ${offset}%`;
      gradientStr += `, ${colorStr}`;
    });

    // Close the gradient string
    gradientStr += ')';

    return gradientStr;
  }

  return (
    <ContextMenu content={<MoodContextMenuContent moodId={props.moodId} />}>
      {({ isOpen, onContextMenu }) => {
        return (
          <div
            {...button.buttonProps}
            className={styles.root}
            ref={mergeRefs([dragRef, dropRef, buttonRef])}
            aria-haspopup="menu"
            aria-expanded={props.isOpen}
            data-is-dragging={isDragging}
            onContextMenu={onContextMenu}
          >
            <CanvasLiquidLinearCircle id={mood.id} size={circleSize} mixedColors={mixedColors} />

            {elements}

            <div className={styles.section}>
              <div className={styles.title}>
                <TextSmallMedium className={styles.titleText}>{mood.name}</TextSmallMedium>
              </div>
              <div className={styles.subtitle}>
                <TextTiny className={styles.subtitleText} color={theme.color.text_light}>
                  {Object.values(mood.devices).length} Lights
                </TextTiny>
              </div>
            </div>
          </div>
        );
      }}
    </ContextMenu>
  );
}

export const MemoizedMood = memo(Mood);
